/**
 * Photographs of our own rooms, shipped with the app.
 *
 * ── WHY THESE ARE NOT CATALOGUE ASSETS ───────────────────────────────────
 * Every other picture on the site is an `asset` row somebody uploaded in the
 * panel, and a room's `cover` is one of those. These are different in kind: a
 * studio is a fixed thing FLOU owns, photographed once, and the pictures change
 * roughly never. Putting them in `public/` means they are versioned with the
 * code that lays them out, they survive a database reset, and a new deploy
 * cannot land with the rooms suddenly blank.
 *
 * The room's uploaded `cover` still wins when there is one — see `RoomPhoto`.
 * This is the floor, not an override: it guarantees a room is never a grey box
 * on a page selling it.
 *
 * File names and the crop each one is chosen for are documented in
 * `public/studio/README.md`. Sized to 1600px and encoded as WebP, because a
 * 3 MB source PNG on a card that renders at 600px is three megabytes nobody
 * asked for.
 */
export type StudioPhoto = { src: string; alt: string }

const STUDIO_PHOTOS: Record<string, readonly StudioPhoto[]> = {
  rozmowa: [
    {
      src: '/studio/rozmowa-2.webp',
      alt: 'Studio Rozmowa — biała kanapa, dwa mikrofony na wysięgnikach i stolik, ujęcie na wprost.',
    },
    {
      src: '/studio/rozmowa-1.webp',
      alt: 'Studio Rozmowa — plan ogólny z lampami panelowymi i zasłonami na całej ścianie.',
    },
  ],
  solo: [
    {
      src: '/studio/solo-2.webp',
      alt: 'Studio Solo — biurko z mikrofonem i laptopem przed ścianą z trzech ekranów, ujęcie na wprost.',
    },
    {
      src: '/studio/solo-1.webp',
      alt: 'Studio Solo — plan ogólny z podświetlanymi regałami i niebieską ścianą ekranów.',
    },
  ],
}

/**
 * The room's own photographs, best frame first.
 *
 * Empty for a room nobody has photographed, which the card handles — it is the
 * same state as a missing cover and draws the same labelled frame.
 */
export function studioPhotos(slug: string): readonly StudioPhoto[] {
  return STUDIO_PHOTOS[slug] ?? []
}
